"use client";

import { useState, useMemo, useRef, useEffect } from "react";
import { Search, X, ChevronLeft } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { getImageUrl } from "@/lib/utils/image";

export default function SearchSelect({ 
  items = [], 
  selectedItem, 
  onSelect, 
  placeholder = "جستجو...", 
  noResultsText = "موردی یافت نشد",
  loading = false,
  loadingText = "در حال بارگذاری...",
  className = "container-base max-w-6xl mx-auto",
  onSearchChange,
  searchValue,
  minSearchLength = 0,
  total,
  showBorder = true,
  viewAllLink,
  viewAllText = "مشاهده همه نتایج",
  onEnter,
  showResultCount = true
}) {
  const initialLabel = useMemo(() => {
    if (!selectedItem) return "";
    return typeof selectedItem === "string" ? selectedItem : selectedItem.label || selectedItem.name || "";
  }, [selectedItem]);

  const [searchQuery, setSearchQuery] = useState(searchValue !== undefined ? searchValue : initialLabel);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (searchValue !== undefined) {
      setSearchQuery(searchValue);
    }
  }, [searchValue]);

  useEffect(() => {
    if (selectedItem !== undefined && searchValue === undefined) {
      setSearchQuery(initialLabel);
    }
  }, [initialLabel, selectedItem, searchValue]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredItems = useMemo(() => {
    if (!items) return [];
    if (searchQuery.length < minSearchLength) return [];
    
    const query = String(searchQuery || "").toLowerCase();
    return items.filter((item) => {
      const label = String(typeof item === "string" ? item : item.label || item.name || "").toLowerCase();
      return label.includes(query);
    });
  }, [items, searchQuery, minSearchLength]);

  const handleSelectItem = (item) => {
    onSelect?.(item);
    const label = typeof item === "string" ? item : item.label || item.name || "";
    if (searchValue === undefined) {
      setSearchQuery(String(label || ""));
    }
    setIsDropdownOpen(false);
  };

  const clearSelection = () => {
    onSelect?.(null);
    if (searchValue === undefined) {
      setSearchQuery("");
    }
    onSearchChange?.("");
  };

  const resultCount = total !== undefined && total !== null ? total : filteredItems.length;
  const statusColor = useMemo(() => {
    if (!showResultCount) return null;
    if (searchQuery.length < minSearchLength || searchQuery === "") return null;
    if (loading) return null;
    return resultCount > 0 ? '#5EC972' : '#E15130';
  }, [showResultCount, searchQuery, resultCount, loading, minSearchLength]);

  const colorClasses = useMemo(() => {
    if (!statusColor) return "text-mute placeholder:text-mute";
    if (statusColor === '#5EC972') return "text-[#5EC972] placeholder:text-[#5EC972]";
    return "text-[#E15130] placeholder:text-[#E15130]";
  }, [statusColor]);

  const inputTextColor = useMemo(() => {
    if (!statusColor) return "text-white";
    return statusColor === '#5EC972' ? "text-[#5EC972]" : "text-[#E15130]";
  }, [statusColor]);

  return (
    <div className={`w-full relative ${className}`} ref={dropdownRef}>
      <div className="relative group">
        <input
          type="text"
          placeholder={placeholder}
          value={searchQuery}
          onChange={(e) => {
            const val = e.target.value;
            setSearchQuery(val);
            onSearchChange?.(val);
            setIsDropdownOpen(true);
          }}
          onFocus={() => setIsDropdownOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && searchQuery.trim()) {
              onEnter?.(searchQuery);
            }
          }}
          className={`w-full h-12 text-sm font-dana-light px-12 rounded-2xl glass-box 
                   ${showBorder ? 'border border-white focus:border-white/20' : 'border-0'}
                   focus:outline-none transition-all text-right
                   ${inputTextColor} ${colorClasses.split(' ').find(c => c.startsWith('placeholder:'))}`}
        />
        <Search 
          className={`absolute right-4 top-1/2 -translate-y-1/2 transition-colors ${statusColor ? '' : 'group-focus-within:text-white'} ${colorClasses.split(' ').find(c => !c.startsWith('placeholder:'))}`}
          style={statusColor ? { color: statusColor } : {}}
          size={20} 
        />
        <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center gap-3">
          {showResultCount && searchQuery.length > 0 && searchQuery.length >= minSearchLength && (
            <span 
              className={`text-sm font-dana-light transition-colors ${statusColor ? '' : 'text-mute'}`}
              style={statusColor ? { color: statusColor } : {}}
            >
              {resultCount} نتیجه
            </span>
          )}
          {selectedItem && (
            <button
              onClick={clearSelection}
              type="button"
              className="text-mute hover:text-white transition-colors"
            >
              <X size={18} />
            </button>
          )}
        </div>

        {isDropdownOpen && (loading || filteredItems.length > 0 || searchQuery.length >= minSearchLength) && (
          <div className="absolute top-full right-0 mt-1 w-full z-[100] animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="!rounded-[20px] max-h-60 overflow-hidden !block border border-white/10 shadow-2xl glass-box  ">
              <div className="w-full max-h-56 overflow-y-auto custom-scrollbar-dark py-2 px-1">
                {loading ? (
                  <div className="p-4 !px-7 text-center text-mute text-sm">{loadingText}</div>
                ) : filteredItems.length > 0 ? (
                  <div className="flex flex-col !pe-3 !ps-5">
                    {filteredItems.map((item) => {
                      const label = typeof item === "string" ? item : item.label || item.name;
                      const value = typeof item === "string" ? item : item.id || item.value || label;
                      
                      return (
                        <button
                          key={value}
                          type="button"
                          onClick={() => handleSelectItem(item)}
                          className={`  w-full text-right !px-3 py-3 border-b border-[#383C42] last:border-0 transition-colors text-sm flex items-center gap-3 group/product
                            ${selectedItem === item 
                              ? 'bg-white/20 text-white ' 
                              : 'text-mute  hover:text-white'
                            }`}
                        >
                          {item.images?.[0] && (
                            <div className="relative w-16 h-16 shrink-0 rounded-xl overflow-hidden bg-white/5 p-1">
                              <Image
                                src={getImageUrl(item.images[0]?.url || item.images[0])}
                                alt={label}
                                fill
                                className="object-contain"
                                sizes="64px"
                              />
                            </div>
                          )}
                          <span className="flex-1 text-right">{label}</span>
                          <div className="flex items-center gap-1 text-xs text-white/40 whitespace-nowrap group-hover/product:text-white transition-colors">
                            <span>مشاهده محصول</span>
                            <ChevronLeft size={16} />
                          </div>
                        </button>
                      );
                    })}
                    
                    {viewAllLink && (total > 10 || filteredItems.length >= 10) && (
                      <Link 
                        href={viewAllLink}
                        className="w-full block text-center py-4 mt-2 border-t border-[#383C42] text-sm text-white/70 hover:text-white transition-colors font-dana-medium bg-white/5 hover:bg-white/10 rounded-b-xl"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        {viewAllText}
                      </Link>
                    )}
                  </div>
                ) : searchQuery.length >= minSearchLength ? (
                  <div className="p-4 text-center text-mute text-sm">{noResultsText}</div>
                ) : null}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
